/**
 * USD to Shares Calculation
 *
 * Efficiently calculates the optimal number of shares that can be purchased
 * for a given USD budget using Newton-Raphson iteration.
 *
 * For LMSR markets, the cost function is:
 * - Monotonically increasing (more shares = higher cost)
 * - Smooth and differentiable
 * - Convex (marginal price increases as you buy)
 *
 * Newton-Raphson converges quadratically for such functions,
 * typically reaching full precision in 2-3 iterations.
 */
import type { Address } from "viem"
import { readContract } from "viem/actions"
import { breakoutMarketAbi } from "../abi/generated/breakoutMarket"
import type { ReadableClient } from "./contracts"
import { fromUsdcScale, toShareScale } from "./convert"

export interface UsdToSharesResult {
	/** Optimal share amount (human-readable) */
	shares: number
	/** Actual cost for these shares (human-readable USDC) */
	cost: number
	/** Fee for these shares (human-readable USDC) */
	fee: number
	/** Total cost including fee (human-readable USDC) */
	total: number
}

/** Minimum tradeable share amount (6 decimals) */
const MIN_SHARES = 0.000001

/** Maximum iterations - Newton converges fast, so 4 is plenty */
const MAX_ITERATIONS = 4

/** Stop when we're within 0.1% of target */
const PRECISION_RATIO = 0.001

/** Stop when we're within 1 cent */
const PRECISION_ABSOLUTE = 0.01

/** Minimum USD amount for calculations (in dollars) */
export const MIN_USD_AMOUNT = 0.01

/** Maximum USD amount to prevent overflow (in dollars) */
const MAX_USD_AMOUNT = 1_000_000_000 // $1 billion

/**
 * Calculate the optimal number of shares for a given USD budget.
 *
 * Algorithm: Newton-Raphson root finding
 *
 * We want to find `shares` such that `cost(shares) = targetUsd`
 * Let f(s) = cost(s) - targetUsd, we want f(s) = 0
 *
 * Newton update: s_new = s - f(s) / f'(s)
 *              = s - (cost(s) - target) / marginalPrice(s)
 *
 * We approximate f'(s) using avgPrice = cost/shares, which is
 * accurate for LMSR since the cost function is smooth.
 *
 * Convergence: Quadratic (doubles correct digits each iteration)
 * Typical iterations: 2-3
 * RPC calls: 1 (prices) + 2-3 (quotes) = 3-4 total
 *
 * @param client - Viem public client
 * @param market - Market contract address
 * @param bucket - Bucket index (0-indexed)
 * @param usdAmount - Target USD amount to spend (human-readable)
 * @param feeBps - Fee in basis points
 * @param cachedPrices - Optional cached prices to avoid duplicate RPC calls
 * @returns Optimal shares and actual cost
 */
export async function calculateSharesForUsd(
	client: ReadableClient,
	market: Address,
	bucket: number,
	usdAmount: number,
	feeBps: number,
	cachedPrices?: readonly bigint[],
): Promise<UsdToSharesResult> {
	// Validate input: NaN, Infinity, negative
	if (!Number.isFinite(usdAmount) || usdAmount <= 0) {
		return { shares: 0, cost: 0, fee: 0, total: 0 }
	}

	// Edge case: very small amount (less than 1 cent)
	if (usdAmount < PRECISION_ABSOLUTE) {
		return { shares: 0, cost: 0, fee: 0, total: 0 }
	}

	// Edge case: prevent overflow on extremely large amounts
	if (usdAmount > MAX_USD_AMOUNT) {
		throw new Error(`Amount exceeds maximum: $${MAX_USD_AMOUNT.toLocaleString()}`)
	}

	const targetUsd = usdAmount
	const feeRate = feeBps / 10000

	// Use cached prices if provided, otherwise fetch
	const prices =
		cachedPrices ??
		(await readContract(client, {
			address: market,
			abi: breakoutMarketAbi,
			functionName: "getPrices",
		}))

	const priceScaled = prices[bucket]
	if (priceScaled === undefined) {
		throw new Error(`Invalid bucket ${bucket}`)
	}

	// Convert price to 0-1 range
	const price = Number(priceScaled) / 1e18

	// Calculate initial estimate
	// For LMSR: total ≈ shares * avgPrice * (1 + feeRate)
	// avgPrice is between currentPrice and finalPrice
	// We use currentPrice * 1.05 as initial estimate (5% price impact buffer)
	const effectivePrice = Math.max(0.001, price) * 1.05 * (1 + feeRate)
	let shares = targetUsd / effectivePrice

	// Track best result that's within budget
	let bestResult: UsdToSharesResult | null = null

	// Newton-Raphson iteration
	for (let i = 0; i < MAX_ITERATIONS; i++) {
		shares = Math.max(MIN_SHARES, shares)

		// Get quote for current estimate
		const quote = await getQuote(client, market, bucket, shares)

		if (!quote) {
			// Quote failed (shares too large for liquidity) - reduce and retry
			shares = shares * 0.5
			continue
		}

		const cost = fromUsdcScale(quote.cost)
		const fee = fromUsdcScale(quote.fee)
		const total = fromUsdcScale(quote.total)

		// Track best result within budget
		if (total <= targetUsd) {
			if (!bestResult || shares > bestResult.shares) {
				bestResult = { shares, cost, fee, total }
			}
		}

		// Check convergence
		const error = total - targetUsd
		const absError = Math.abs(error)

		if (absError < PRECISION_ABSOLUTE || absError / targetUsd < PRECISION_RATIO) {
			// Converged - return if within budget, else best result
			if (total <= targetUsd) {
				return { shares, cost, fee, total }
			}
			// Slightly over budget - return best under-budget result
			if (bestResult) {
				return bestResult
			}
		}

		// Newton-Raphson step
		// f(s) = total(s) - target
		// f'(s) ≈ avgPrice * (1 + feeRate) = total / shares
		// s_new = s - f(s) / f'(s) = s - (total - target) / (total / s)
		//       = s - s * (total - target) / total
		//       = s * (1 - (total - target) / total)
		//       = s * target / total
		//
		// But this is just proportional scaling! Newton IS proportional for this case.
		// However, we can do better by using cost (without fee) for the derivative:
		// f'(s) ≈ avgCostPerShare * (1 + feeRate) = (cost/s) * (1 + feeRate)
		//
		// s_new = s - (total - target) / ((cost/s) * (1 + feeRate))
		//       = s - s * (total - target) / (cost * (1 + feeRate))

		const derivative = (cost / shares) * (1 + feeRate)
		const newShares = shares - error / derivative

		// Safety: ensure we're making progress and staying positive
		if (newShares <= 0 || Math.abs(newShares - shares) < MIN_SHARES) {
			break
		}

		shares = newShares
	}

	return bestResult ?? { shares: 0, cost: 0, fee: 0, total: 0 }
}

/**
 * Get a quote for a specific share amount.
 */
async function getQuote(
	client: ReadableClient,
	market: Address,
	bucket: number,
	shares: number,
): Promise<{ cost: bigint; fee: bigint; total: bigint } | null> {
	try {
		const sharesScaled = toShareScale(shares)
		const result = await readContract(client, {
			address: market,
			abi: breakoutMarketAbi,
			functionName: "quoteBuy",
			args: [bucket, sharesScaled],
		})

		return {
			cost: result[0],
			fee: result[1],
			total: result[2],
		}
	} catch {
		return null
	}
}
